import React, { useState, lazy, createElement } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { boats, Boat } from '../data/boats';
import { PREMIUM_BOATS_AS_BOATS } from '../data/premiumBoats';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { ChevronRight, RotateCcw, MessageCircle, Ship, Users, Euro } from 'lucide-react';
type Answer = string | null;
/** Helper: parse the cheapest € price from a Boat's price list. Handles "1.750€", "350€", etc. */
const cheapestPrice = (boat: Boat): number => {
  const nums = boat.prices.map(p => {
    const cleaned = p.price.replace(/[€\s]/g, '')
    // Spanish thousands separator "." => strip when followed by 3 digits
    .replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.');
    const n = parseFloat(cleaned);
    return isNaN(n) ? Infinity : n;
  });
  return Math.min(...nums);
};
/** Helper: parse capacity as a number (boat.specs.capacity is a string). */
const capacityNum = (boat: Boat): number => {
  const n = parseInt(boat.specs.capacity, 10);
  return isNaN(n) ? 0 : n;
};
/**
 * Score a boat 0..100 against the user's quiz answers.
 * Higher = better match. Boats below SCORE_THRESHOLD are filtered out.
 */
const scoreBoat = (boat: Boat, people: string | null, license: string | null, budget: string | null): number => {
  let score = 0;
  const cap = capacityNum(boat);
  const price = cheapestPrice(boat);
  // ── CAPACITY (max 35 pts) ─────────────────────────────────────────
  // We prefer boats whose capacity *covers* the requested group with a
  // little headroom but isn't wildly oversized.
  const peopleMin: Record<string, number> = {
    '1-4': 1,
    '5-6': 5,
    '7-8': 7,
    '9-12': 9
  };
  const peopleMax: Record<string, number> = {
    '1-4': 4,
    '5-6': 6,
    '7-8': 8,
    '9-12': 12
  };
  if (people && peopleMin[people] !== undefined) {
    const reqMin = peopleMin[people];
    const reqMax = peopleMax[people];
    if (cap >= reqMax) {
      // Boat fits — bonus for being close in size (avoid huge yacht for 2 people)
      const oversize = cap - reqMax;
      score += Math.max(15, 35 - oversize * 3);
    } else if (cap >= reqMin) {
      // Boat fits the lower bound but not the full group — partial credit
      score += 10;
    }
    // Boat too small (cap < reqMin) → no points
  }
  // ── LICENSE (max 30 pts) ──────────────────────────────────────────
  // Eligibility is based on whether the CUSTOMER actually needs their own
  // license — NOT on the category label. A boat that doesn't require a
  // license (e.g. Nireus, category "sin-licencia") can be used by everyone,
  // with or without a license. Premium boats include a skipper, so the
  // customer never needs their own license either.
  const needsCustomerLicense = boat.specs.licenseReq === true && boat.category !== 'premium';
  if (license === 'yes') {
    // Customer has a license — every boat is valid. Slight edge to boats that
    // make use of that license, but no-license boats still score well.
    score += needsCustomerLicense ? 30 : 25;
  } else if (license === 'no') {
    // No license — any boat that doesn't require the customer's own license
    // is valid: sin-licencia boats (incl. Nireus) AND skipper-included premium.
    if (!needsCustomerLicense) {
      score += 30;
    }
    // Boats requiring the customer's own license → 0 pts (disqualified).
  }
  // ── BUDGET (max 35 pts) ───────────────────────────────────────────
  // Match the boat's cheapest price tier against the user's budget bracket.
  const budgetMax: Record<string, number> = {
    '150': 150,
    '300': 300,
    '500': 500,
    '500+': Infinity
  };
  if (budget && budgetMax[budget] !== undefined) {
    const capLimit = budgetMax[budget];
    if (price <= capLimit) {
      // In-budget: more points for being closer to the top of the bracket
      // (i.e. the user gets the most boat for their money).
      const headroom = capLimit - price;
      if (budget === '500+') {
        // For the "luxe" bracket, prefer the more expensive boats.
        score += price >= 500 ? 35 : 15;
      } else {
        score += Math.max(15, 35 - Math.floor(headroom / 10));
      }
    }
    // Over-budget → 0 pts
  }
  return score;
};
const SCORE_THRESHOLD = 30;
const MAX_RESULTS = 6;
export const BoatFinderQuiz = () => {
  const {
    t
  } = useLanguage();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([null, null, null, null]);
  const [results, setResults] = useState<{
    type: 'boats' | 'jetski' | 'license';
    data?: Boat[];
  } | null>(null);
  const questions = [{
    id: 'q1',
    title: t('quiz.q1'),
    icon: Users,
    options: [{
      id: '1-4',
      label: t('quiz.q1.o1')
    }, {
      id: '5-6',
      label: t('quiz.q1.o2')
    }, {
      id: '7-8',
      label: t('quiz.q1.o3')
    }, {
      id: '9-12',
      label: t('quiz.q1.o4')
    }]
  }, {
    id: 'q2',
    title: t('quiz.q2'),
    icon: Ship,
    options: [{
      id: 'yes',
      label: t('quiz.q2.o1')
    }, {
      id: 'no',
      label: t('quiz.q2.o2')
    }, {
      id: 'want',
      label: t('quiz.q2.o3')
    }]
  }, {
    id: 'q3',
    title: t('quiz.q3'),
    icon: Ship,
    options: [{
      id: 'boat',
      label: t('quiz.q3.o1')
    }, {
      id: 'both',
      label: t('quiz.q3.o2')
    }, {
      id: 'jetski',
      label: t('quiz.q3.o3')
    }, {
      id: 'surprise',
      label: t('quiz.q3.o4')
    }]
  }, {
    id: 'q4',
    title: t('quiz.q4'),
    icon: Euro,
    options: [{
      id: '150',
      label: t('quiz.q4.o1')
    }, {
      id: '300',
      label: t('quiz.q4.o2')
    }, {
      id: '500',
      label: t('quiz.q4.o3')
    }, {
      id: '500+',
      label: t('quiz.q4.o4')
    }]
  }];
  const handleAnswer = (optionId: string) => {
    const newAnswers = [...answers];
    newAnswers[step] = optionId;
    setAnswers(newAnswers);
    if (step < 3) {
      setStep(step + 1);
    } else {
      calculateResults(newAnswers);
    }
  };
  const calculateResults = (finalAnswers: Answer[]) => {
    const [people, license, experience, budget] = finalAnswers;
    // Short-circuit routes — Jet ski and license info pages.
    if (experience === 'jetski') {
      setResults({
        type: 'jetski'
      });
      return;
    }
    if (license === 'want') {
      setResults({
        type: 'license'
      });
      return;
    }
    // Unified catalog: FleetCatalog boats + Yates Premium.
    const allBoats: Boat[] = [...boats, ...PREMIUM_BOATS_AS_BOATS];
    // Score every boat and keep the strong matches.
    const scored = allBoats.map(boat => ({
      boat,
      score: scoreBoat(boat, people, license, budget)
    })).filter(({
      score
    }) => score >= SCORE_THRESHOLD).sort((a, b) => b.score - a.score);
    let recommendedBoats: Boat[] = scored.slice(0, MAX_RESULTS).map(s => s.boat);
    // Safety net: if scoring rejected everything (unusual answer combination),
    // fall back to "any boat that respects the license requirement".
    if (recommendedBoats.length === 0) {
      recommendedBoats = allBoats.filter(b => license === 'no' ? b.category === 'sin-licencia' || b.category === 'premium' : true).slice(0, MAX_RESULTS);
    }
    setResults({
      type: 'boats',
      data: recommendedBoats
    });
  };
  const resetQuiz = () => {
    setStep(0);
    setAnswers([null, null, null, null]);
    setResults(null);
  };
  const handleWhatsApp = (boat?: Boat) => {
    let message = '';
    if (results?.type === 'jetski') {
      message = t('wa.jetski').replace('[FECHA]', '___').replace('[NÚMERO]', '___');
    } else if (results?.type === 'license') {
      message = t('wa.license');
    } else if (boat) {
      const templateKey = boat.waTemplateKey ?? 'wa.general';
      message = t(templateKey).replace('[BARCO]', boat.name).replace('[FECHA]', '___').replace('[NÚMERO]', '___').replace('[SÍ/NO]', answers[1] === 'yes' ? 'Sí' : 'No').replace('[PRESUPUESTO]', '___');
    }
    window.open(getWhatsAppUrl(message), '_blank');
  };
  return <section className="py-12 sm:py-16 md:py-20 bg-card" data-id="element-19">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20">
        <div className="text-center mb-8 sm:mb-12" data-id="element-21">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-22">
            {t('quiz.title')}
          </h2>
          <p className="text-gray-600" data-id="element-23">{t('quiz.subtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-10 border border-gray-100" data-id="element-24">
          {!results ? <div data-id="element-25">
              {/* Progress Bar */}
              <div className="flex justify-between mb-8" data-id="element-26">
                {[0, 1, 2, 3].map(i => <div key={i} className="flex-1 flex items-center" data-id="element-27">
                    <div className={`h-2 rounded-full flex-1 ${i <= step ? 'bg-turquoise' : 'bg-gray-100'}`} data-id="element-28"></div>
                    {i < 3 && <div className="w-2" data-id="element-29"></div>}
                  </div>)}
              </div>

              {/* Question */}
              <div className="mb-8" data-id="element-30">
                <div className="flex items-center space-x-3 mb-6 text-navy" data-id="element-31">
                  {createElement(questions[step].icon, {
                className: 'w-6 h-6 text-turquoise'
              })}
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold leading-snug" data-id="element-32">
                    {questions[step].title}
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-33">
                  {questions[step].options.map(option => <button key={option.id} onClick={() => handleAnswer(option.id)} className="text-left px-4 sm:px-6 py-3 sm:py-4 rounded-xl border-2 border-gray-100 hover:border-turquoise hover:bg-turquoise/5 transition-all font-medium text-sm sm:text-base text-gray-700 hover:text-navy flex justify-between items-center group" data-id="element-34">
                      <span data-id="element-35">{option.label}</span>
                      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-turquoise transition-colors" data-id="element-36" />
                    </button>)}
                </div>
              </div>
            </div> : <div className="animate-fade-in" data-id="element-37">
              <div className="text-center mb-8" data-id="element-38">
                <h3 className="text-2xl font-bold text-navy mb-2" data-id="element-39">
                  {t('quiz.result.title')}
                </h3>
                <button onClick={resetQuiz} className="text-sm text-gray-500 hover:text-navy flex items-center justify-center space-x-1 mx-auto" data-id="element-40">
                  <RotateCcw className="w-4 h-4" data-id="element-41" />
                  <span data-id="element-42">{t('quiz.reset')}</span>
                </button>
              </div>

              {results.type === 'jetski' && <div className="text-center bg-card p-8 rounded-xl" data-id="element-43">
                  <h4 className="text-xl font-bold text-navy mb-4" data-id="element-44">
                    {t('jetski.title')}
                  </h4>
                  <p className="text-gray-600 mb-6" data-id="element-45">{t('jetski.desc')}</p>
                  <button onClick={() => handleWhatsApp()} className="bg-turquoise text-navy font-bold py-3 px-8 rounded-full flex items-center justify-center space-x-2 mx-auto hover:scale-105 transition-transform" data-id="element-46">
                    <MessageCircle className="w-5 h-5" data-id="element-47" />
                    <span data-id="element-48">{t('jetski.cta')}</span>
                  </button>
                </div>}

              {results.type === 'license' && <div className="text-center bg-card p-8 rounded-xl" data-id="element-49">
                  <h4 className="text-xl font-bold text-navy mb-4" data-id="element-50">
                    {t('license.title')}
                  </h4>
                  <p className="text-gray-600 mb-6" data-id="element-51">{t('license.desc')}</p>
                  <button onClick={() => handleWhatsApp()} className="bg-turquoise text-navy font-bold py-3 px-8 rounded-full flex items-center justify-center space-x-2 mx-auto hover:scale-105 transition-transform" data-id="element-52">
                    <MessageCircle className="w-5 h-5" data-id="element-53" />
                    <span data-id="element-54">{t('license.cta')}</span>
                  </button>
                </div>}

              {results.type === 'boats' && results.data && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-55">
                  {results.data.map(boat => {
              const isPremium = boat.category === 'premium';
              const cheapest = cheapestPrice(boat);
              return <div key={boat.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col" data-id="element-56">
                        <div className="relative" data-id="element-57">
                          <img src={boat.image} alt={boat.name} loading="lazy" decoding="async" className="w-full h-48 object-cover" data-id="element-58" />
                          {isPremium && <div className="absolute top-2 right-2 bg-[#C9A84C] text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-md" data-id="element-59">
                              {t('premium.badge')}
                            </div>}
                        </div>
                        <div className="p-4 flex flex-col flex-1" data-id="element-60">
                          <div className="flex items-baseline justify-between gap-2 mb-2" data-id="element-61">
                            <h4 className="font-bold text-navy text-lg uppercase" data-id="element-62">
                              {boat.name}
                            </h4>
                            <span className="text-xs text-gray-500 whitespace-nowrap" data-id="element-63">
                              {boat.specs.capacity} {t('boat.spec.peopleMax')}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mb-3 line-clamp-2" data-id="element-64">
                            {t(boat.descKey)}
                          </p>
                          <div className="mb-4 text-xs text-gray-600" data-id="element-65">
                            <span className="font-semibold" data-id="element-66">
                              {t('quiz.result.from')}{' '}
                            </span>
                            <span className="text-navy font-bold text-base" data-id="element-67">
                              {Number.isFinite(cheapest) ? `${cheapest}€` : boat.prices[0]?.price}
                            </span>
                          </div>
                          <button onClick={() => handleWhatsApp(boat)} className="mt-auto w-full bg-navy text-white font-bold py-2.5 px-4 rounded-lg flex items-center justify-center space-x-2 hover:bg-navy-light transition-colors text-sm" data-id="element-68">
                            <MessageCircle className="w-4 h-4 text-turquoise" data-id="element-69" />
                            <span data-id="element-70">{t('quiz.result.cta')}</span>
                          </button>
                        </div>
                      </div>;
            })}
                </div>}
            </div>}
        </div>
      </div>
    </section>;
};